/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  ApplePayDomainRequest,
  ApplePayDomains,
  ConnectStripeDirectKeysRequest,
  DeleteAdminV1StoresStoreIdPaymentMethodsIdPathParameters,
  DeleteAdminV1StoresStoreIdPaymentsStripeApplePayDomainsDomainPathParameters,
  DeleteAdminV1StoresStoreIdPaymentsStripePathParameters,
  GetAdminV1StoresStoreIdPaymentMethods200,
  GetAdminV1StoresStoreIdPaymentMethodsPathParameters,
  GetAdminV1StoresStoreIdPaymentsStripeApplePayDomainsPathParameters,
  GetAdminV1StoresStoreIdPaymentsStripePathParameters,
  ManualPaymentMethod,
  PatchAdminV1StoresStoreIdPaymentMethodsIdBody,
  PatchAdminV1StoresStoreIdPaymentMethodsIdPathParameters,
  PostAdminV1StoresStoreIdPaymentMethodsBody,
  PostAdminV1StoresStoreIdPaymentMethodsPathParameters,
  PostAdminV1StoresStoreIdPaymentsStripeApplePayDomainsPathParameters,
  PostAdminV1StoresStoreIdPaymentsStripeConnectCallbackPathParameters,
  PostAdminV1StoresStoreIdPaymentsStripeConnectPathParameters,
  PostAdminV1StoresStoreIdPaymentsStripeDirectKeysPathParameters,
  PutAdminV1StoresStoreIdPaymentsStripeWebhookSecretPathParameters,
  StripeAccount,
  StripeConnectCallbackRequest,
  StripeConnectStart,
  StripeWebhookSecretRequest
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * @summary Manual payment methods of the store, in display order
 */
export const getAdminV1StoresStoreIdPaymentMethods = (
    { storeId }: GetAdminV1StoresStoreIdPaymentMethodsPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdPaymentMethods200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdPaymentMethods200>(
      {url: `/admin/v1/stores/${storeId}/payment-methods`, method: 'GET'
    },
      options);
    }
  /**
 * Orders paid this way are placed unpaid and marked paid by staff (D-031)
 * @summary Add a manual payment method such as cash on delivery or bank transfer
 */
export const postAdminV1StoresStoreIdPaymentMethods = (
    { storeId }: PostAdminV1StoresStoreIdPaymentMethodsPathParameters,
    postAdminV1StoresStoreIdPaymentMethodsBody: BodyType<PostAdminV1StoresStoreIdPaymentMethodsBody>,
 options?: SecondParameter<typeof customInstance<ManualPaymentMethod>>,) => {
      return customInstance<ManualPaymentMethod>(
      {url: `/admin/v1/stores/${storeId}/payment-methods`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdPaymentMethodsBody
    },
      options);
    }
  /**
 * @summary Update a manual payment method
 */
export const patchAdminV1StoresStoreIdPaymentMethodsId = (
    { storeId, id }: PatchAdminV1StoresStoreIdPaymentMethodsIdPathParameters,
    patchAdminV1StoresStoreIdPaymentMethodsIdBody: BodyType<PatchAdminV1StoresStoreIdPaymentMethodsIdBody>,
 options?: SecondParameter<typeof customInstance<ManualPaymentMethod>>,) => {
      return customInstance<ManualPaymentMethod>(
      {url: `/admin/v1/stores/${storeId}/payment-methods/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdPaymentMethodsIdBody
    },
      options);
    }
  /**
 * @summary Delete a manual payment method (409 while unpaid orders use it)
 */
export const deleteAdminV1StoresStoreIdPaymentMethodsId = (
    { storeId, id }: DeleteAdminV1StoresStoreIdPaymentMethodsIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/payment-methods/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * `webhookUrl` is the endpoint to register in the tenant's Stripe dashboard in direct-keys mode; `webhookSecretSet` says whether its signing secret is stored
 * @summary Whether and how the store is connected to Stripe
 */
export const getAdminV1StoresStoreIdPaymentsStripe = (
    { storeId }: GetAdminV1StoresStoreIdPaymentsStripePathParameters,
 options?: SecondParameter<typeof customInstance<StripeAccount>>,) => {
      return customInstance<StripeAccount>(
      {url: `/admin/v1/stores/${storeId}/payments/stripe`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Disconnect Stripe; card payments stop being offered, past payments keep their record
 */
export const deleteAdminV1StoresStoreIdPaymentsStripe = (
    { storeId }: DeleteAdminV1StoresStoreIdPaymentsStripePathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/payments/stripe`, method: 'DELETE'
    },
      options);
    }
  /**
 * The secret key is checked against Stripe, then stored encrypted; the webhook secret is what the tenant's endpoint at `webhookUrl` signs with. Posting again rotates the keys without disconnecting and keeps the stored webhook secret unless a new one is given. 422 PAYMENT_PROVIDER_REJECTED when Stripe refuses the key
 * @summary Connect Stripe with the tenant's own API keys (doc 06 option B)
 */
export const postAdminV1StoresStoreIdPaymentsStripeDirectKeys = (
    { storeId }: PostAdminV1StoresStoreIdPaymentsStripeDirectKeysPathParameters,
    connectStripeDirectKeysRequest: BodyType<ConnectStripeDirectKeysRequest>,
 options?: SecondParameter<typeof customInstance<StripeAccount>>,) => {
      return customInstance<StripeAccount>(
      {url: `/admin/v1/stores/${storeId}/payments/stripe/direct-keys`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: connectStripeDirectKeysRequest
    },
      options);
    }
  /**
 * Stored encrypted, never returned. 409 PAYMENT_PROVIDER_UNAVAILABLE while Stripe is not connected; 409 CONFLICT on a Connect account, which the platform endpoint verifies
 * @summary Set or rotate the webhook signing secret of a direct-keys connection
 */
export const putAdminV1StoresStoreIdPaymentsStripeWebhookSecret = (
    { storeId }: PutAdminV1StoresStoreIdPaymentsStripeWebhookSecretPathParameters,
    stripeWebhookSecretRequest: BodyType<StripeWebhookSecretRequest>,
 options?: SecondParameter<typeof customInstance<StripeAccount>>,) => {
      return customInstance<StripeAccount>(
      {url: `/admin/v1/stores/${storeId}/payments/stripe/webhook-secret`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: stripeWebhookSecretRequest
    },
      options);
    }
  /**
 * 409 PAYMENT_PROVIDER_UNAVAILABLE until the platform has a Connect client id
 * @summary Start Stripe Connect (doc 06 option A): the URL to send the owner to
 */
export const postAdminV1StoresStoreIdPaymentsStripeConnect = (
    { storeId }: PostAdminV1StoresStoreIdPaymentsStripeConnectPathParameters,
 options?: SecondParameter<typeof customInstance<StripeConnectStart>>,) => {
      return customInstance<StripeConnectStart>(
      {url: `/admin/v1/stores/${storeId}/payments/stripe/connect`, method: 'POST'
    },
      options);
    }
  /**
 * @summary Finish Stripe Connect with the code Stripe sent back
 */
export const postAdminV1StoresStoreIdPaymentsStripeConnectCallback = (
    { storeId }: PostAdminV1StoresStoreIdPaymentsStripeConnectCallbackPathParameters,
    stripeConnectCallbackRequest: BodyType<StripeConnectCallbackRequest>,
 options?: SecondParameter<typeof customInstance<StripeAccount>>,) => {
      return customInstance<StripeAccount>(
      {url: `/admin/v1/stores/${storeId}/payments/stripe/connect/callback`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: stripeConnectCallbackRequest
    },
      options);
    }
  /**
 * Empty while Stripe is not connected
 * @summary Storefront domains registered for Apple Pay on the connected account
 */
export const getAdminV1StoresStoreIdPaymentsStripeApplePayDomains = (
    { storeId }: GetAdminV1StoresStoreIdPaymentsStripeApplePayDomainsPathParameters,
 options?: SecondParameter<typeof customInstance<ApplePayDomains>>,) => {
      return customInstance<ApplePayDomains>(
      {url: `/admin/v1/stores/${storeId}/payments/stripe/apple-pay-domains`, method: 'GET'
    },
      options);
    }
  /**
 * The storefront must already serve Stripe's file at /.well-known/apple-developer-merchantid-domain-association. Registered domains appear in `capabilities.applePayDomains`; 422 when Stripe cannot verify the domain
 * @summary Register a storefront domain for Apple Pay on the connected account
 */
export const postAdminV1StoresStoreIdPaymentsStripeApplePayDomains = (
    { storeId }: PostAdminV1StoresStoreIdPaymentsStripeApplePayDomainsPathParameters,
    applePayDomainRequest: BodyType<ApplePayDomainRequest>,
 options?: SecondParameter<typeof customInstance<StripeAccount>>,) => {
      return customInstance<StripeAccount>(
      {url: `/admin/v1/stores/${storeId}/payments/stripe/apple-pay-domains`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: applePayDomainRequest
    },
      options);
    }
  /**
 * 404 when the domain is not registered for this store; 409 PAYMENT_PROVIDER_UNAVAILABLE while Stripe is not connected
 * @summary Remove a domain's Apple Pay registration
 */
export const deleteAdminV1StoresStoreIdPaymentsStripeApplePayDomainsDomain = (
    { storeId, domain }: DeleteAdminV1StoresStoreIdPaymentsStripeApplePayDomainsDomainPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/payments/stripe/apple-pay-domains/${domain}`, method: 'DELETE'
    },
      options);
    }
  export type GetAdminV1StoresStoreIdPaymentMethodsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdPaymentMethods>>>
export type PostAdminV1StoresStoreIdPaymentMethodsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdPaymentMethods>>>
export type PatchAdminV1StoresStoreIdPaymentMethodsIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdPaymentMethodsId>>>
export type DeleteAdminV1StoresStoreIdPaymentMethodsIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdPaymentMethodsId>>>
export type GetAdminV1StoresStoreIdPaymentsStripeResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdPaymentsStripe>>>
export type DeleteAdminV1StoresStoreIdPaymentsStripeResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdPaymentsStripe>>>
export type PostAdminV1StoresStoreIdPaymentsStripeDirectKeysResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdPaymentsStripeDirectKeys>>>
export type PutAdminV1StoresStoreIdPaymentsStripeWebhookSecretResult = NonNullable<Awaited<ReturnType<typeof putAdminV1StoresStoreIdPaymentsStripeWebhookSecret>>>
export type PostAdminV1StoresStoreIdPaymentsStripeConnectResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdPaymentsStripeConnect>>>
export type PostAdminV1StoresStoreIdPaymentsStripeConnectCallbackResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdPaymentsStripeConnectCallback>>>
export type GetAdminV1StoresStoreIdPaymentsStripeApplePayDomainsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdPaymentsStripeApplePayDomains>>>
export type PostAdminV1StoresStoreIdPaymentsStripeApplePayDomainsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdPaymentsStripeApplePayDomains>>>
export type DeleteAdminV1StoresStoreIdPaymentsStripeApplePayDomainsDomainResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdPaymentsStripeApplePayDomainsDomain>>>
